/**
 * Presentation bands for a journey completion rate.
 *
 * These are reporting status colours rather than tenant branding, so they are
 * fixed values. The accents are deliberately softened siblings of the status
 * chart palette, so the headline figure reads as part of the same chart rather
 * than as an alarm state. Where the accent is too light on white, the figure
 * uses a darkened sibling so it stays legible.
 */
export interface UsageCompletionBand {
  /** Wash and progress bar fill. */
  accentColour: string;
  /** Percentage figure. */
  textColour: string;
  /** Diagonal wash drawn behind the card content. */
  washGradient: string;
}

interface BandThreshold {
  /** Upper bound of the band, exclusive. */
  maxExclusive: number;
  accentColour: string;
  textColour: string;
}

const bandThresholds: BandThreshold[] = [
  { maxExclusive: 50, accentColour: "#F5877A", textColour: "#A65246" },
  { maxExclusive: 90, accentColour: "#F09D16", textColour: "#F09D16" },
  {
    maxExclusive: Number.POSITIVE_INFINITY,
    accentColour: "#A9D15C",
    textColour: "#61832F",
  },
];

const withAlpha = (hexColour: string, alpha: number): string => {
  const hex = hexColour.replace("#", "");
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const getCompletionBand = (
  completionPercent: number,
): UsageCompletionBand => {
  const band =
    bandThresholds.find(
      (candidate) => completionPercent < candidate.maxExclusive,
    ) ?? bandThresholds[bandThresholds.length - 1];

  return {
    accentColour: band.accentColour,
    textColour: band.textColour,
    washGradient: `linear-gradient(160deg, ${withAlpha(
      band.accentColour,
      0.14,
    )} 0%, ${withAlpha(band.accentColour, 0.03)} 55%, transparent 100%)`,
  };
};
